import React, { useEffect, useState } from 'react';
import { useForm, get, FieldValues } from 'react-hook-form';
import { saveAs } from 'file-saver';
import { useSelector, useDispatch } from 'react-redux';
import { format } from 'date-fns';
import { ErrorField, StyledSelect } from '../../lib/HooksFormFields';
import styles from './ExportCompare.module.scss';
import { exportXlsxAction, getExportLists } from '../../actions/export';
import { validateExportYears } from '../../utils';
import useExportYearPeriod from '../../hooks/useExportYearPeriod';

const ExportCompare = () => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const userAuth = useSelector((store: any) => store.authReducer)?.user;

  const {
    exportList,
  } = useSelector((store: any) => store.shiftsReducer);

  const {
    control,
    formState: { errors },
    handleSubmit,
    watch,
    reset,
    setValue,
  } = useForm<FieldValues>();

  const form = watch();

  const { options } = useExportYearPeriod({
    watch,
    setValue,
    exportList,
  });

  async function onSubmit(values: FieldValues) {
    setIsLoading(true);
    setApiError(null);
    const selectedPoles = values.poles as string[] | undefined;
    const poles = options.poles
      .filter((p) => selectedPoles?.includes(p.value) && p.isPole)
      .map((p) => p.value);
    const startDate = format(new Date(values.startDate), 'yyyy-MM-dd');
    const endDate = format(new Date(values.endDate), 'yyyy-MM-dd');
    const data = {
      type: values.type,
      startDate,
      endDate,
      structure: values.structure,
      poles,
      users: values.users || [],
    };
    try {
      const startMonth = options.startDate
        .find((d) => d.value === values.startDate)?.label.replace(' ', '-');
      const endMonth = options.endDate
        .find((d) => d.value === values.endDate)?.label.replace(' ', '-');
      const structure = exportList?.structures
        ?.find((u: { value: string }) => u.value === data.structure)?.label;
      let name = 'analyse';
      if (structure) {
        name += `-${structure.replaceAll(' ', '-')}`;
      }
      if (startMonth === endMonth) {
        name += `${startMonth}`;
      } else {
        name += `-${startMonth}-${endMonth}`;
      }
      const res : any = await exportXlsxAction(dispatch, data);
      if (res) {
        const blob = new Blob([res]);
        const filename = `${name}.xlsx`;
        saveAs(blob, filename);
      }
      setIsLoading(false);
    } catch (err) {
      console.error(err);
      setIsLoading(false);
      setApiError('Une erreur c\'est produite lors de l\'export');
    }
  }

  useEffect(() => {
    getExportLists(dispatch);
  }, []);

  useEffect(() => {
    const currentYear = exportList?.years?.find((y: { isCurrent: boolean }) => y.isCurrent);
    if (currentYear) {
      reset({
        type: 'compare',
        structure: localStorage.getItem('structure'),
        years: [currentYear.value],
      });
    }
  }, [exportList?.years]);

  return (
    <div className={styles.modale}>
      <div className={styles.form}>
        <div className={styles.row}>
        {(userAuth.role === 'Admin' || userAuth.role === 'Owner')
          && <div className={styles['container-field']}>
            <StyledSelect
                label={'Structure'}
                name="structure"
                control={control}
                options={exportList.structures}
                rules={{
                  required: 'la structure est requise',
                }}
            />
            {typeof get(errors, 'structure')?.message === 'string' && (
              <ErrorField message={get(errors, 'structure')?.message || ''}/>
            )}
          </div>
        }
        <div className={styles['container-field']}>
          <StyledSelect
              name="years"
              label="Année(s)"
              control={control}
              isMulti
              maxSelections={2}
              options={exportList?.years}
              rules={{
                validate: (value: number[] | undefined) => validateExportYears(value),
              }}
          />
          {typeof get(errors, 'years')?.message === 'string' && (
            <ErrorField message={get(errors, 'years')?.message || ''}/>
          )}
        </div>
      </div>
      {form.years?.length > 0
        && <div className={styles.row}>
          <div className={styles['container-field']}>
            <StyledSelect
                name="startDate"
                label={'Mois de départ'}
                control={control}
                options={options.startDate}
                rules={{
                  required: 'le mois de départ est requis',
                }}
            />
            {typeof get(errors, 'startDate')?.message === 'string' && (
              <ErrorField message={get(errors, 'startDate')?.message || ''}/>
            )}
          </div>
          <div className={styles['container-field']}>
            <StyledSelect
                name="endDate"
                label={'Mois de fin'}
                control={control}
                options={options.endDate}
                rules={{
                  required: 'le mois de fin est requis',
                }}
            />
            {typeof get(errors, 'endDate')?.message === 'string' && (
              <ErrorField message={get(errors, 'endDate')?.message || ''}/>
            )}
          </div>
        </div>
      }
      {form?.endDate
        && <div className={styles.row}>
          <div className={styles['container-field']}>
            <StyledSelect
              name="users"
              label={'Membres'}
              control={control}
              isMulti={true}
              options={options.users}
            />
          </div>
          <div className={styles['container-field']}>
            <StyledSelect
              name="poles"
              label={'Pôles'}
              control={control}
              isMulti={true}
              options={options.poles}
            />
          </div>
        </div>
      }
      {apiError && (
          <div className={styles.error}>
            <ErrorField message={apiError}/>
          </div>
      )}
      {form?.endDate
      && <>
        {!isLoading
          ? <div className={styles.actions}>
            <button
              type="button"
              onClick={handleSubmit(onSubmit)}
            >
              Exporter
            </button>
          </div>
          : <div className={styles.actions}>
            Export en cours...
          </div>
        }
        </>
      }
    </div>
    </div>
  );
};

export default ExportCompare;
